// services
import AnalyticsService from 'modules/Analytics/service';
import FetchService from 'modules/Fetch/service';
import Search from 'modules/Search';
// constants
import { EVENTS } from 'modules/Analytics/consts';
// typings
import { PreRiskWarningData } from 'modules/TradingChart/components/TradingView/components/SurveillanceMessages/typings';
import { PreRiskData } from './typings';

class PreRiskService {
  private fetchPreRiskDataForMultipleScrips = async (
    instrumentKeys: string[],
  ): Promise<PreRiskData[] | null> => {
    try {
      const result = await FetchService.request<'PRE_RISK', PreRiskData[]>(
        'PRE_RISK',
        'v3/validate',
        'GET_PRE_RISK',
        {
          method: 'POST',
          body: { instrumentKeys },
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return result.data || [];
    } catch (error) {
      AnalyticsService.sendEvent(EVENTS.PRE_RISK_ERROR_FOR_MULTIPLE_ORDERS, {
        Error: error || 'Pre Risk Warning Error Multiple Orders.',
      });
      return null;
    }
  };

  public getPreRiskChecksForMultipleScrips = async (
    instrumentKeys: string[],
  ): Promise<{
    surveillanceData: PreRiskData[];
    openPreRiskModal: boolean;
  }> => {
    const preRiskData = await
      this.fetchPreRiskDataForMultipleScrips(instrumentKeys);

    if (preRiskData) {
      const { surveillanceData, hasSurveillanceItems } = preRiskData.reduce(
        (acc, item) => {
          const filteredValidation = item.validation.filter(
            (validationItem) => validationItem.category === 'SURVEILLANCE',
          );

          if (filteredValidation.length > 0) {
            acc.surveillanceData.push({
              ...item,
              validation: filteredValidation,
            });
            acc.hasSurveillanceItems = true;
          }

          return acc;
        },
        { surveillanceData: [] as PreRiskData[], hasSurveillanceItems: false },
      );

      return {
        surveillanceData,
        openPreRiskModal: hasSurveillanceItems,
      };
    }

    return {
      surveillanceData: [],
      openPreRiskModal: false,
    };
  };

  public getPreRiskForSingleScrip = async (
    instrumentKey: string,
  ): Promise<PreRiskData | null> => {
    try {
      const preRiskData = (
        await this.fetchPreRiskDataForMultipleScrips([instrumentKey]))?.[0];
      return preRiskData ?? null;
    } catch (e) {
      AnalyticsService.sendEvent(
        EVENTS.PRE_RISK_ERROR,
        { instrumentKey },
        true,
      );
      return null;
    }
  };

  public getPreRiskWarningForSingleScrip = async (instrumentKey: string) => {
    const preRiskData = await this.getPreRiskForSingleScrip(instrumentKey);

    const surveillanceMessage = preRiskData?.validation?.find(
      (validationItem) =>
        validationItem.category === 'SURVEILLANCE' && validationItem.popup,
    );

    return surveillanceMessage ? surveillanceMessage.message : null;
  };

  public getPreRiskWarningForMultipleScrips = async (
    basketOrderInstrumentKeys: string[],
  ): Promise<{
    openPreRiskModal: boolean;
    surveillanceMessages: Array<PreRiskWarningData>;
    learnMore: string;
    surveillanceData: PreRiskData[];
  }> => {
    if (basketOrderInstrumentKeys.length === 0) {
      return this.defaultResponse();
    }

    try {
      const result = await
        this.getPreRiskChecksForMultipleScrips(basketOrderInstrumentKeys);

      if (!result) {
        return this.defaultResponse();
      }

      const { surveillanceData, openPreRiskModal } = result;

      const learnMore = this.extractLearnMoreLink(surveillanceData);
      const surveillanceMessages = await
        this.buildSurveillanceMessages(surveillanceData);

      return {
        openPreRiskModal,
        surveillanceMessages,
        learnMore,
        surveillanceData,
};
    } catch (error) {
      return this.defaultResponse();
    }
  };

  // Default response when there are no open instruments or errors
  private defaultResponse() {
    return { openPreRiskModal: false, surveillanceMessages: [], learnMore: '', surveillanceData: [] };
  }

  // Extract the learn more link from surveillance data
  private extractLearnMoreLink(surveillanceData: PreRiskData[]): string {
    return (
      surveillanceData
        .flatMap(item => item.validation)
        .find(({ category }) => category === 'SURVEILLANCE')?.hl ?? ''
    );
  }

  // Build surveillance messages from surveillance data
  private async buildSurveillanceMessages(
    surveillanceData: PreRiskData[],
  ): Promise<PreRiskWarningData[]> {
    return Promise.all(
      surveillanceData.map(async (item) => {
        const instrumentKey = await
          Search.searchByInstrumentKey(item.instrumentKey);
        const symbol = instrumentKey?.displayName || '';
        const message = item.validation?.find(({ category }) => category === 'SURVEILLANCE')?.message || '';

        return {
          message,
          symbol,
          instrumentKey: item.instrumentKey,
        } as PreRiskWarningData;
      }),
    );
  }
}

export default new PreRiskService();
